import './payment-method-add-sheet.scss';

import Script from 'next/script';
import { ReactElement, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { AddressSelectSupporter } from '@sola/ark-core/molecules/address-select/address-select-supporter.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import {
  FormSubmit,
  FormSubmitProps,
} from '@sola/ark-core/molecules/form/form.tsx';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { usePaymentMethodAdd } from '@sola/my-sdk/payment-methods/hooks.ts';
import { AddressSelectOptions } from '@sola/schema/dotcom/shared/address.ts';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.ts';
import {
  PaymentMethodTypesEnum,
  SupporterAchDetails,
  SupporterCCDetails,
  SupporterCCFieldsEnum,
  SupporterPaymentMethodDetails,
} from '@sola/schema/dotcom/supporters/paymentMethods.ts';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getLongExpiryDate } from '@sola/shared-utils/credit-cards.ts';
import { userErrorPaymentMethodCreate } from '@sola/shared-utils/errors/supporters.ts';
import {
  isPaymentMethodAch,
  isPaymentMethodCard,
} from '@sola/shared-utils/my/payment-method-type-guards.ts';
import { useSupporterBillingAddresses } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { getNonRecurringCommitments } from '@sola/supporter-sdk/utils/commitments.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts.tsx';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
import { PaymentACHFields } from '../../molecules/payment-method-add-form/payment-ach-fields.tsx';
import { PaymentCCFields } from '../../molecules/payment-method-add-form/payment-cc-fields.tsx';
import { PaymentMethodAddForm } from '../../molecules/payment-method-add-form/payment-method-add-form.tsx';
import { PaymentMethodSwitch } from '../../molecules/payment-method-switch/payment-method-switch.tsx';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import {
  createPaymentMethodPartialSuccessAlert,
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts.tsx';
import { getErrorTranslationKeys } from '../../utils/errors.ts';
import {
  RecurringGivingOptions,
  RecurringGivingRadioOptionsEnum,
} from '../recurring-giving-options/recurring-giving-options.tsx';

type PaymentMethodAddSheetFooterProps = Pick<FormSubmitProps, 'isBusy'>;

const AddPaymentMethodSheetFooter: React.FC<
  PaymentMethodAddSheetFooterProps
> = ({ isBusy }) => {
  const { t } = useTranslation('core');

  return (
    <SheetFooter>
      <SheetActions>
        <SheetClose asChild>
          <ButtonStyle
            size="lg"
            purpose="neutral"
            variant="secondary"
            disabled={isBusy}
            fullWidth
          >
            <button type="button">{t('core:actions.cancel')}</button>
          </ButtonStyle>
        </SheetClose>

        <FormSubmit
          iconBefore={<ArkIcon iconName="lock-01" />}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('core:actions.add')}
        </FormSubmit>
      </SheetActions>
    </SheetFooter>
  );
};

export interface PaymentMethodAddSheetProps {
  commitments?: GraphQLCommitment[];
  /**
   * This should only take in elements that resolve to html buttons.
   */
  openTrigger: ReactElement;
  onComplete?: (
    newPaymentMethod: GraphQLSupporterPaymentMethod,
  ) => Promise<void>;
}
export const PaymentMethodAddSheet: React.FC<PaymentMethodAddSheetProps> = ({
  openTrigger,
  commitments = [],
  onComplete,
}) => {
  const { t } = useTranslation('my');
  const title = t('addPaymentMethod.form.title');
  const [isOpen, setIsOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const { alerts: localAlerts, addAlerts, clearAlerts } = useLocalAlerts();
  const {
    alerts: sheetAlerts,
    setAlerts: setSheetAlerts,
    clearAlerts: clearSheetAlerts,
    removeAlert: removeSheetAlert,
    handleOpenChange,
  } = useModalAlerts();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodTypesEnum>(
    PaymentMethodTypesEnum.Ach,
  );
  const nonRecurringCommitments = getNonRecurringCommitments(commitments);

  const addPaymentMethod = usePaymentMethodAdd();
  const { supporterAddresses } = useSupporterBillingAddresses();

  const handleSubmit = async (
    paymentMethodDetails: SupporterPaymentMethodDetails,
    data: FieldValues,
  ) => {
    clearSheetAlerts();
    clearAlerts();
    setIsBusy(true);

    try {
      let commitmentIds = [] as string[];

      if (data.recurringGiving === RecurringGivingRadioOptionsEnum.Yes) {
        commitmentIds =
          nonRecurringCommitments
            ?.map((commitment) => commitment.internalId)
            .filter((id): id is string => typeof id === 'string') ?? [];
      }

      const { paymentMethod: addedPaymentMethod, isPartialSuccess } =
        await addPaymentMethod.mutateAsync({
          paymentMethod: paymentMethodDetails,
          commitmentIds,
        });

      if (!addedPaymentMethod) {
        throw userErrorPaymentMethodCreate();
      }

      const alerts = [
        createPaymentMethodSuccessAlert(
          addedPaymentMethod,
          'addPaymentMethod.success.action',
        ),
      ];

      if (isPartialSuccess) {
        alerts.push(createPaymentMethodPartialSuccessAlert());
      }

      addAlerts(alerts);

      if (onComplete) {
        await onComplete(addedPaymentMethod);
      }

      handleOpenChange(false, setIsOpen);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodCreate,
        errors,
      );
      setSheetAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }

    setIsBusy(false);
  };

  const handleAchSubmit = async (data: FieldValues) => {
    const achDetails = data as SupporterAchDetails;
    await handleSubmit(
      { ...achDetails, type: PaymentMethodTypesEnum.Ach },
      data,
    );
  };

  const handleCardSubmit = async (data: FieldValues) => {
    const { addressOption, ...rest } = data as SupporterCCDetails;
    let cardDetails = rest;
    const expirationDate = getLongExpiryDate(
      data[SupporterCCFieldsEnum.expirationDate],
    );

    if (addressOption !== AddressSelectOptions.Add) {
      const supporterAddress = supporterAddresses?.get(addressOption);
      cardDetails = {
        ...cardDetails,
        ...supporterAddress?.billingAddress,
      };
    }

    await handleSubmit(
      {
        ...cardDetails,
        addressOption,
        expirationDate,
        type: PaymentMethodTypesEnum.Card,
      },
      data,
    );
  };

  const handleClose = (e: Event) => {
    if (localAlerts?.length) {
      // There are alerts, don't steal the focus
      e.preventDefault();
    }
  };

  return (
    <>
      <Script src="/tokenex.js" strategy="lazyOnload" />
      <Sheet
        open={isOpen}
        onOpenChange={(open) => handleOpenChange(open, setIsOpen)}
      >
        <SheetTrigger>{openTrigger}</SheetTrigger>
        <SheetContent title={title} onCloseAutoFocus={handleClose}>
          <SheetHeader>{title}</SheetHeader>
          <SheetBody>
            <p
              id="selectPaymentMethodType"
              className="body-sm md:body-md body-neutral-mild"
            >
              {t('addPaymentMethod.form.leadin')}
            </p>

            {sheetAlerts.length > 0 && (
              <div className="mt-comfy-sm md:mt-comfy-lg">
                {sheetAlerts.map((sheetAlert) => (
                  <LocalAlert
                    key={sheetAlert.id}
                    alert={sheetAlert}
                    className="my-comfy-xs"
                    onClose={removeSheetAlert}
                  />
                ))}
              </div>
            )}

            <form aria-describedby="selectPaymentMethodType">
              <PaymentMethodSwitch
                setSelectedPaymentMethod={setSelectedMethod}
              />
            </form>

            <div className={isPaymentMethodAch(selectedMethod) ? '' : 'hidden'}>
              <Alert
                variant="positive"
                title={t('addPaymentMethod.alert.title')}
                message={t('addPaymentMethod.alert.message')}
              />

              <Divider purpose="neutral" className="my-comfy-xl" />

              <PaymentMethodAddForm onSubmit={handleAchSubmit}>
                <SectionContent
                  heading={t('payments.methodForm.formTitle.bank')}
                >
                  <Fieldset id="ach-fields">
                    <PaymentACHFields />
                  </Fieldset>
                </SectionContent>

                <Divider purpose="neutral" className="my-comfy-xl" />

                {/* Recurring giving options */}
                {!!nonRecurringCommitments?.length && (
                  <SectionContent
                    heading={t('payments.methodForm.formTitle.recurringGiving')}
                  >
                    <RecurringGivingOptions
                      commitments={nonRecurringCommitments}
                    />
                  </SectionContent>
                )}

                <AddPaymentMethodSheetFooter isBusy={isBusy} />
              </PaymentMethodAddForm>
            </div>

            <div
              className={isPaymentMethodCard(selectedMethod) ? '' : 'hidden'}
            >
              <Divider purpose="neutral" className="my-comfy-xl" />
              <PaymentMethodAddForm onSubmit={handleCardSubmit}>
                <div>
                  <SectionContent
                    heading={t('payments.methodForm.formTitle.card')}
                  >
                    <Fieldset id="cc-fields">
                      <PaymentCCFields />
                    </Fieldset>
                  </SectionContent>

                  <Divider purpose="neutral" className="my-comfy-xl" />

                  <SectionContent
                    heading={t('payments.methodForm.formTitle.billingInfo')}
                  >
                    <AddressSelectSupporter
                      label={t('payments.methodForm.fieldLabels.billingInfo')}
                    />
                  </SectionContent>
                </div>

                <Divider purpose="neutral" className="my-comfy-xl" />

                {/* Recurring giving options */}
                {!!nonRecurringCommitments?.length && (
                  <SectionContent
                    heading={t('payments.methodForm.formTitle.recurringGiving')}
                  >
                    <RecurringGivingOptions
                      commitments={nonRecurringCommitments}
                    />
                  </SectionContent>
                )}

                <AddPaymentMethodSheetFooter isBusy={isBusy} />
              </PaymentMethodAddForm>
            </div>
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
};
